/**
 * DocIntel Application Router
 *
 * Defines all top-level routes:
 * - /login           Public login page
 * - /admin/*         Platform admin routes (requires platform_admin role)
 * - /portal/*        Tenant portal routes (Phase 3 placeholder)
 * - /                Redirects based on user role
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useAuth } from './auth/AuthProvider';
import RequireAuth from './auth/RequireAuth';
import LoginPage from './auth/LoginPage';

// ---------------------------------------------------------------------------
// Admin pages (lazy-loaded)
// ---------------------------------------------------------------------------
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const DashboardPage = lazy(() => import('./admin/DashboardPage'));
const TenantsPage = lazy(() => import('./admin/TenantsPage'));
const TenantDetailPage = lazy(() => import('./admin/TenantDetailPage'));
const GcpProjectsPage = lazy(() => import('./admin/GcpProjectsPage'));
const DocTypesPage = lazy(() => import('./admin/DocTypesPage'));
const UsagePage = lazy(() => import('./admin/UsagePage'));
const JobsPage = lazy(() => import('./admin/JobsPage'));
const StoragePage = lazy(() => import('./admin/StoragePage'));
const AuditLogPage = lazy(() => import('./admin/AuditLogPage'));
const UsersPage = lazy(() => import('./admin/UsersPage'));
const ConfigPage = lazy(() => import('./admin/ConfigPage'));
const AdminSchemasPage = lazy(() => import('./admin/SchemasPage'));
const SchemaDetailPage = lazy(() => import('./admin/SchemaDetailPage'));

// ---------------------------------------------------------------------------
// Tenant Portal pages (lazy-loaded)
// ---------------------------------------------------------------------------
const PortalLayout = lazy(() => import('./portal/PortalLayout'));
const PortalDashboard = lazy(() => import('./portal/DashboardPage'));
const PlaygroundPage = lazy(() => import('./portal/PlaygroundPage'));
const JobHistoryPage = lazy(() => import('./portal/JobHistoryPage'));
const JobDetailPage = lazy(() => import('./portal/JobDetailPage'));
const SchemasPage = lazy(() => import('./portal/SchemasPage'));
const PortalSchemaDetailPage = lazy(() => import('./portal/SchemaDetailPage'));
const PortalUsagePage = lazy(() => import('./portal/UsagePage'));
const SettingsPage = lazy(() => import('./portal/SettingsPage'));
const MembersPage = lazy(() => import('./portal/MembersPage'));
const ProcessorsPage = lazy(() => import('./portal/ProcessorsPage'));

/** Suspense fallback shown while lazy chunks load. */
function PageLoader() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      role="status"
      aria-label="Loading page"
    >
      <CircularProgress />
    </Box>
  );
}

/** Root redirect: sends admins to /admin/dashboard, others to /portal/dashboard. */
function RootRedirect() {
  const { isAdmin } = useAuth();
  return <Navigate to={isAdmin ? '/admin/dashboard' : '/portal/dashboard'} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />

        {/* Admin routes */}
        <Route
          path="/admin/*"
          element={
            <RequireAuth role="platform_admin">
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="gcp-projects" element={<GcpProjectsPage />} />
          <Route path="doc-types" element={<DocTypesPage />} />
          <Route path="schemas" element={<AdminSchemasPage />} />
          <Route path="schemas/:id" element={<SchemaDetailPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="storage" element={<StoragePage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="config" element={<ConfigPage />} />
        </Route>

        {/* Tenant Portal routes */}
        <Route
          path="/portal/*"
          element={
            <RequireAuth>
              <PortalLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboard />} />
          <Route path="playground" element={<PlaygroundPage />} />
          <Route path="history" element={<JobHistoryPage />} />
          <Route path="history/:jobId" element={<JobDetailPage />} />
          <Route path="schemas" element={<SchemasPage />} />
          <Route path="schemas/:id" element={<PortalSchemaDetailPage />} />
          <Route path="usage" element={<PortalUsagePage />} />
          <Route path="processors" element={<ProcessorsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="members" element={<MembersPage />} />
        </Route>

        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
